// The only way the engine touches time. The browser gets the real globals (the e2e fixtures warp
// Date/setTimeout/setInterval and page.clock fakes them, so they must be read at call time, never
// bound at module load). Vitest injects a fake.
export interface Scheduler {
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(id: number): void;
  setInterval(fn: () => void, ms: number): number;
  clearInterval(id: number): void;
  now(): number;
}

export const realScheduler: Scheduler = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
  clearTimeout: (id) => globalThis.clearTimeout(id),
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms) as unknown as number,
  clearInterval: (id) => globalThis.clearInterval(id),
  now: () => Date.now(),
};

/** A deterministic scheduler for tests: `advance(ms)` runs due timers in order. */
export function createFakeScheduler(start = 0): Scheduler & { advance(ms: number): void; pending(): number } {
  let now = start;
  let nextId = 1;
  type Timer = { id: number; at: number; fn: () => void; every: number | null };
  let timers: Timer[] = [];
  const fire = (until: number) => {
    for (;;) {
      const due = timers.filter((t) => t.at <= until).sort((a, b) => a.at - b.at || a.id - b.id)[0];
      if (!due) break;
      now = due.at;
      if (due.every === null) timers = timers.filter((t) => t !== due);
      else due.at += due.every;
      due.fn();
    }
    now = until;
  };
  return {
    setTimeout: (fn, ms) => {
      const id = nextId++;
      timers.push({ id, at: now + ms, fn, every: null });
      return id;
    },
    clearTimeout: (id) => {
      timers = timers.filter((t) => t.id !== id);
    },
    setInterval: (fn, ms) => {
      const id = nextId++;
      timers.push({ id, at: now + ms, fn, every: Math.max(1, ms) });
      return id;
    },
    clearInterval: (id) => {
      timers = timers.filter((t) => t.id !== id);
    },
    now: () => now,
    advance: (ms) => fire(now + ms),
    pending: () => timers.length,
  };
}
